import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {Id, View} from "@khanacademy/wonder-blocks-core";
import {Spring} from "@khanacademy/wonder-blocks-layout";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    LabelMedium,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import {isValidEmail, type UpdateParentEmailStep} from "@khan/auth-shared";
import {logUnreachableCode} from "@khan/logging";
import {mediaQueries} from "@khan/shared-styles";

import ResendUnderAgeGateApprovalEmailMutation, {
    type ResendUnderAgeGateApprovalEmailResponse,
} from "../graphql/resend-uag-approval-email-mutation.tsx";
import UpdateUnderAgeGateParentEmailMutation, {
    type UpdateUnderAgeGateParentEmail,
} from "../graphql/update-under-age-gate-parent-email-mutation.tsx";

const u13ModalInitialStrings = {
    contentString1: (email: string): string =>
        t`Your parent or guardian needs to click “approve” in the email sent to ${`<strong>${email}</strong>`}.`,

    contentString2: (days: string): string =>
        t`If they don’t, your account will be deactivated in ${`<strong>${days}</strong>`}. You will lose all your badges and energy points.`,
} as const;

const u13ModalUpdateStrings = {
    contentString1: (email: string): string =>
        t`The original email we sent went to ${`<strong>${email}</strong>`}.`,
} as const;

const u13ModalSentStrings = {
    contentString1: (email: string): string =>
        t`An email was just sent to your parent or guardian at ${`<strong>${email}</strong>`}. All they need to do is click “approve” in the email sent to them.`,
} as const;

const defaultSendState = {
    resendEmailStatus: "unsent",
    emailInvalid: true,
} as const;

const getHeading = (step: UpdateParentEmailStep): string => {
    switch (step) {
        case "updateEmail":
            return t`Update your parent or guardian’s email`;
        case "resendSuccess":
            return t`We sent another email to your parent or guardian`;
        case "initial":
            return t`Remind your parent or guardian to check their email`;
        default:
            throw logUnreachableCode("Invalid step", step);
    }
};

type OwnProps = {
    daysLeftString: string;
    parentEmail: string;
    onUpdateParentEmail: (parentEmail: string) => void;
    onClose?: () => unknown;
    initialStep?: UpdateParentEmailStep;
};

type Props = OwnProps & WithMarkConversionProps;

type State = {
    step: UpdateParentEmailStep;
    resendEmailStatus: "unsent" | "sending" | "sent" | "failed";
    parentEmail: string;
    emailInvalid: boolean;
};

class U13ReminderModal extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            step: props.initialStep || "initial",
            parentEmail: props.parentEmail,
            ...defaultSendState,
        };
    }

    componentDidMount() {
        this._isMounted = true;
        this.props.markConversion({id: "u13_reminder_modal_shown"});
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    _isMounted = false;

    handleUpdateStep: (step: UpdateParentEmailStep) => void = (
        step: UpdateParentEmailStep,
    ) => {
        this.setState({
            step,
            ...defaultSendState,
        });
    };

    handleEmailChange: (event: React.ChangeEvent<HTMLInputElement>) => void = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const newParentEmail = event.target.value;
        const {parentEmail} = this.props;

        const emailInvalid =
            !isValidEmail(newParentEmail) && newParentEmail !== parentEmail;

        this.setState({emailInvalid, parentEmail: newParentEmail});
    };

    createHandleUpdateEmail: (
        updateParentEmail: UpdateUnderAgeGateParentEmail,
    ) => () => void =
        (updateParentEmail: UpdateUnderAgeGateParentEmail) => () => {
            const {onUpdateParentEmail} = this.props;

            updateParentEmail(this.state.parentEmail);
            onUpdateParentEmail(this.state.parentEmail);
        };

    handleFormKeyDown: (event: React.KeyboardEvent<HTMLFormElement>) => void = (
        event: React.KeyboardEvent<HTMLFormElement>,
    ) => {
        if (event.key === "Enter") {
            // This stops the form from trying to automatically submit and refresh the page.
            event.preventDefault();
        }
    };

    createHandleFormKeyUp: (
        updateParentEmail: UpdateUnderAgeGateParentEmail,
    ) => (event: React.KeyboardEvent<HTMLFormElement>) => void =
        (updateParentEmail: UpdateUnderAgeGateParentEmail) =>
        (event: React.KeyboardEvent<HTMLFormElement>) => {
            const {resendEmailStatus, emailInvalid, parentEmail} = this.state;
            const {onUpdateParentEmail} = this.props;

            if (event.key === "Enter") {
                // This stops the form from trying to automatically submit and refresh the page.
                event.preventDefault();

                if (emailInvalid || resendEmailStatus === "sending") {
                    return;
                }

                updateParentEmail(parentEmail);
                onUpdateParentEmail(parentEmail);
            }
        };

    onResendCompleted: (
        arg1?: ResendUnderAgeGateApprovalEmailResponse | null | undefined,
    ) => void = (data) => {
        const errorCode = data?.error?.code;
        if (errorCode) {
            this.onResendFail();
        } else {
            this.onResendSuccess();
        }
    };

    onResendSuccess: () => void = () => {
        if (this._isMounted) {
            this.handleUpdateStep("resendSuccess");
        }
    };

    onResendFail: () => void = () => {
        if (this._isMounted) {
            this.setState({resendEmailStatus: "failed"});
        }
    };

    renderEmailUpdateForm: () => React.ReactNode = () => {
        return (
            <Id>
                {(newEmailInputId) => (
                    <UpdateUnderAgeGateParentEmailMutation
                        onError={this.onResendFail}
                        onCompleted={this.onResendSuccess}
                    >
                        {({updateParentEmail}) => (
                            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- TODO: Address a11y error
                            <form
                                onKeyDown={this.handleFormKeyDown}
                                onKeyUp={this.createHandleFormKeyUp(
                                    updateParentEmail,
                                )}
                                autoComplete="on"
                                className={css(styles.form)}
                            >
                                <LabelMedium
                                    htmlFor={newEmailInputId}
                                    tag="label"
                                >
                                    {t`Your parent or guardian’s email`}
                                </LabelMedium>
                                <input
                                    id={newEmailInputId}
                                    placeholder={t`E.g., email@email.com`}
                                    className={css(styles.inputField)}
                                    required={true}
                                    type="email"
                                    onChange={this.handleEmailChange}
                                    autoComplete="email"
                                    name="email"
                                />
                            </form>
                        )}
                    </UpdateUnderAgeGateParentEmailMutation>
                )}
            </Id>
        );
    };

    renderModalContent: () => React.ReactNode = () => {
        const {daysLeftString} = this.props;
        const {parentEmail, step} = this.state;

        if (step === "initial") {
            return (
                <Body>
                    <div
                        className={css(styles.detail)}
                        dangerouslySetInnerHTML={{
                            __html: u13ModalInitialStrings.contentString1(
                                parentEmail,
                            ),
                        }}
                    />
                    <div
                        className={css(styles.detail)}
                        dangerouslySetInnerHTML={{
                            __html: u13ModalInitialStrings.contentString2(
                                daysLeftString,
                            ),
                        }}
                    />
                    <div className={css(styles.detail)}>
                        {t`You can `}

                        <Button
                            onClick={(e) =>
                                this.handleUpdateStep("updateEmail")
                            }
                            kind="tertiary"
                            style={styles.inlineButton}
                        >
                            {t`update their email`}
                        </Button>

                        {t` or `}

                        <ResendUnderAgeGateApprovalEmailMutation
                            onCompleted={this.onResendCompleted}
                            onError={this.onResendFail}
                        >
                            {({resendUnderAgeGateApprovalEmail, isLoading}) => (
                                <Button
                                    onClick={() =>
                                        resendUnderAgeGateApprovalEmail()
                                    }
                                    kind="tertiary"
                                    style={styles.inlineButton}
                                    spinner={isLoading}
                                >
                                    {t`resend the approval email`}
                                </Button>
                            )}
                        </ResendUnderAgeGateApprovalEmailMutation>
                    </div>
                </Body>
            );
        }
        if (step === "updateEmail") {
            return (
                <Body>
                    <div
                        className={css(styles.detail)}
                        dangerouslySetInnerHTML={{
                            __html: u13ModalUpdateStrings.contentString1(
                                // NOTE(diedra): We're getting the parentEmail from props,
                                // not state, here because we're talking about the
                                // last time an email was sent, not the address to which
                                // we will momentarily be sending.
                                this.props.parentEmail,
                            ),
                        }}
                    />
                    <div className={css(styles.detail)}>
                        {t`Enter an updated email address for them and we’ll send them a new email to approve your account.`}
                    </div>
                    {this.renderEmailUpdateForm()}
                </Body>
            );
        }
        return (
            <Body>
                <div
                    className={css(styles.detail)}
                    dangerouslySetInnerHTML={{
                        __html: u13ModalSentStrings.contentString1(parentEmail),
                    }}
                />
            </Body>
        );
    };

    renderFooter: () => React.ReactNode = () => {
        const {onClose} = this.props;
        const {step, resendEmailStatus, emailInvalid} = this.state;

        if (step === "initial") {
            return (
                <View style={styles.modalFooter}>
                    {resendEmailStatus === "failed" && (
                        <div role="alert">
                            <LabelSmall style={styles.saveError}>
                                {t`Oops! Something went wrong. Please try again.`}
                            </LabelSmall>
                        </div>
                    )}
                    <Spring />
                    <Button onClick={onClose}>{t`Got it!`}</Button>
                </View>
            );
        }
        if (step === "updateEmail") {
            return (
                <View style={styles.modalFooter}>
                    {resendEmailStatus === "failed" && (
                        <div role="alert">
                            <LabelSmall style={styles.saveError}>
                                {t`Oops! Something went wrong. Please try again.`}
                            </LabelSmall>
                        </div>
                    )}
                    <Spring />
                    <Button
                        onClick={onClose}
                        kind="tertiary"
                        style={styles.cancelButton}
                    >
                        {t`Cancel`}
                    </Button>
                    <UpdateUnderAgeGateParentEmailMutation
                        onError={this.onResendFail}
                        onCompleted={this.onResendSuccess}
                    >
                        {({updateParentEmail}) => (
                            <Button
                                onClick={this.createHandleUpdateEmail(
                                    updateParentEmail,
                                )}
                                spinner={resendEmailStatus === "sending"}
                                disabled={emailInvalid}
                            >
                                {t`Send email`}
                            </Button>
                        )}
                    </UpdateUnderAgeGateParentEmailMutation>
                </View>
            );
        }
        return (
            <View style={styles.modalFooter}>
                <Button onClick={onClose}>{t`Done`}</Button>
            </View>
        );
    };

    render(): React.ReactNode {
        const {step} = this.state;

        return (
            <OnePaneDialog
                title={getHeading(step)}
                content={this.renderModalContent()}
                footer={this.renderFooter()}
                style={styles.modal}
            />
        );
    }
}

const styles = StyleSheet.create({
    modal: {
        height: "auto",
    },
    detail: {
        ":not(:last-child)": {
            marginBottom: 8,
        },
    },
    inlineButton: {
        // Required to stop the buttons from affecting the line height.
        height: "auto",
    },
    form: {
        marginTop: spacing.medium_16,
        display: "block",
    },
    inputField: {
        padding: `10px ${spacing.medium_16}px`,
        border: `1px solid ${color.offBlack50}`,
        borderRadius: spacing.xxxSmall_4,
        marginTop: spacing.small_12,
        // Styles to match <Body />.
        fontFamily: "Lato, sans-serif",
        fontSize: 16,

        // HACK(diedra): The width styles here makes the input field take up
        // the full space of the modal, minus the padding,
        // while also accounting for the 1px border on either side.
        width: `calc(100% - ${spacing.xLarge_32}px - 2px)`,
        [mediaQueries.lgOrLarger]: {
            width: `calc(100% - ${spacing.xxLarge_48}px - 2px)`,
        },
    },
    modalFooter: {
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
    },
    saveError: {
        color: color.red,
    },
    cancelButton: {
        marginRight: spacing.medium_16,
    },
});

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof U13ReminderModal,
        React.ComponentProps<typeof U13ReminderModal>
    >
>;

/**
 * TODO(somewhatabstract): This file is not properly annotated for learning
 * time, but we've decided it's not currently a priority.
 */
export default withMarkConversion(
    U13ReminderModal,
) as React.ComponentType<ExportProps>;
